import { accessAtLeastManager, accessForAllRoles } from 'charts/common/access';
import { AnalyticsTranslationKeys } from 'intl/keys';
import { ExtendedFieldTranslationKeys } from 'intl/keys/extendedFieldTranslationKeys';
import { InspectionTranslationKeys } from 'intl/keys/inspectionTranslationKeys';
import { ChartType, InspectionChart, IssueChart } from 'shared/types/analytics';
import type { ChartDefinition } from './types';

export const CHART_CATALOG = [
  {
    id: 'originator',
    issueChartKey: IssueChart.originator,
    section: 'issues',
    order: 1,
    type: ChartType.donut,
    size: 'small',
    titleId: AnalyticsTranslationKeys.originators,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleOriginator,
    builderKey: 'originator',
    rendererKey: ChartType.donut,
    enabled: true,
    accessMode: 'hook',
  },
  {
    id: 'rootCause',
    issueChartKey: IssueChart.rootCause,
    section: 'issues',
    order: 2,
    type: ChartType.donut,
    size: 'small',
    titleId: AnalyticsTranslationKeys.rootCauseChart,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleRootCause,
    builderKey: 'rootCause',
    rendererKey: ChartType.donut,
    enabled: true,
    accessMode: 'hook',
  },
  {
    id: 'impact',
    issueChartKey: IssueChart.impact,
    section: 'issues',
    order: 3,
    type: ChartType.donut,
    size: 'small',
    titleId: AnalyticsTranslationKeys.chart,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleImpact,
    builderKey: 'impact',
    rendererKey: ChartType.donut,
    enabled: true,
    accessMode: 'hook',
  },
  {
    id: 'environmentalAspect',
    issueChartKey: IssueChart.environmentalAspect,
    section: 'issues',
    order: 4,
    type: ChartType.donut,
    size: 'small',
    titleId: AnalyticsTranslationKeys.aspectChart,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleEnvironmentalAspect,
    builderKey: 'environmentalAspect',
    rendererKey: ChartType.donut,
    enabled: true,
    accessMode: 'hook',
  },
  {
    id: 'workType',
    issueChartKey: IssueChart.workType,
    section: 'issues',
    order: 5,
    type: ChartType.donut,
    size: 'small',
    titleId: AnalyticsTranslationKeys.workTypeChart,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleWorkType,
    builderKey: 'workType',
    rendererKey: ChartType.donut,
    enabled: true,
    accessMode: 'hook',
  },
  {
    id: 'hsEffect',
    issueChartKey: IssueChart.hsEffect,
    section: 'issues',
    order: 6,
    type: ChartType.bar,
    size: 'small',
    titleId: AnalyticsTranslationKeys.effectChart,
    subtitleId: AnalyticsTranslationKeys.effectChartDangerousConditionsActions,
    builderKey: 'hsEffect',
    rendererKey: ChartType.bar,
    enabled: true,
    accessMode: 'hook',
  },
  {
    id: 'hazardType',
    issueChartKey: IssueChart.hsHazardType,
    section: 'issues',
    order: 7,
    type: ChartType.donut,
    size: 'small',
    titleId: ExtendedFieldTranslationKeys.hazardCategory,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleHazardType,
    builderKey: 'hazardType',
    rendererKey: ChartType.donut,
    enabled: true,
    accessMode: 'hook',
  },
  {
    id: 'completedInspectionsByTemplate',
    inspectionChartKey: InspectionChart.completedInspectionsByTemplate,
    section: 'inspections',
    order: 1,
    type: ChartType.donut,
    size: 'small',
    titleId: AnalyticsTranslationKeys.inspectionsByTemplateChart,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleInspectionsByTemplate,
    builderKey: 'completedInspectionsByTemplate',
    rendererKey: ChartType.donut,
    enabled: true,
    accessMode: 'role',
    canAccess: accessAtLeastManager,
  },
  {
    id: 'inspectionEvolution',
    inspectionChartKey: InspectionChart.inspectionEvolution,
    section: 'inspections',
    order: 2,
    type: ChartType.line,
    size: 'small',
    titleId: InspectionTranslationKeys.evolutionChart,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleInspectionEvolution,
    builderKey: 'inspectionEvolution',
    rendererKey: ChartType.line,
    enabled: true,
    accessMode: 'role',
    canAccess: accessAtLeastManager,
  },
  {
    id: 'inspectionTriplet',
    inspectionChartKey: InspectionChart.inspectionTriplet,
    section: 'inspections',
    order: 3,
    type: ChartType.triplet,
    size: 'small',
    titleId: InspectionTranslationKeys.summaryChart,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleInspectionTriplet,
    builderKey: 'inspectionTriplet',
    rendererKey: ChartType.triplet,
    enabled: true,
    accessMode: 'role',
    canAccess: accessAtLeastManager,
  },
  {
    // Disabled (`enabled: false`). Implementation: `charts/disabled/detectedResolved`.
    id: 'detectedResolved',
    section: 'issues',
    order: 8,
    type: ChartType.bar,
    size: 'small',
    titleId: AnalyticsTranslationKeys.resolvedChart,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleDetectedResolved,
    builderKey: 'detectedResolved',
    rendererKey: ChartType.bar,
    enabled: false,
    accessMode: 'role',
    canAccess: accessForAllRoles,
  },
  {
    // Disabled (`enabled: false`). Implementation: `charts/disabled/resolutionTime`.
    id: 'resolutionTime',
    section: 'issues',
    order: 9,
    type: ChartType.line,
    size: 'small',
    titleId: AnalyticsTranslationKeys.resolutionTimeChart,
    subtitleId: AnalyticsTranslationKeys.cardSubtitleResolutionTime,
    builderKey: 'resolutionTime',
    rendererKey: ChartType.line,
    enabled: false,
    accessMode: 'role',
    canAccess: accessForAllRoles,
  },
] as const satisfies readonly ChartDefinition[];

export type ChartId = (typeof CHART_CATALOG)[number]['id'];
export type ChartSection = (typeof CHART_CATALOG)[number]['section'];

const CATALOG_BY_ID: Record<ChartId, (typeof CHART_CATALOG)[number]> =
  Object.fromEntries(CHART_CATALOG.map((entry) => [entry.id, entry])) as Record<
    ChartId,
    (typeof CHART_CATALOG)[number]
  >;

export function getChartDefinition(
  id: ChartId
): (typeof CHART_CATALOG)[number] | undefined {
  return CATALOG_BY_ID[id];
}

export function getEnabledChartDefinitions(): readonly (typeof CHART_CATALOG)[number][] {
  return CHART_CATALOG.filter((entry) => entry.enabled);
}

export function isChartId(id: string): id is ChartId {
  return CHART_CATALOG.some((entry) => entry.id === id);
}
